import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Modal, Select, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import LikertMatrix from '@/components/LikertMatrix';

import { SCREENING_QUESTION_TYPE_OPTIONS } from '@/constants';

import { IScreeningQuestionsBuilderProps } from '@/interface';

import {
  DEFAULT_LIKERT_OPTIONS,
  DEFAULT_LIKERT_STATEMENTS,
} from '@/pages/dashboard/project/components/useScreeningQuestionsForm';

import { DEFAULT_SEGMENT, SCREENING_QUESTION } from '@/enum/screening-question-type.enum';

export const ScreeningQuestionsBuilder: React.FC<IScreeningQuestionsBuilderProps> = ({
  questions,
  setScreeningQuestions,
  canAdd = true,
  canUpdate = true,
  canDelete = true,
  addScreeningQuestion,
  updateScreeningQuestion,
  removeScreeningQuestion,
  addQuestionOption,
  updateQuestionOption,
  removeQuestionOption,
  addQuestionStatement,
  updateQuestionStatement,
  removeQuestionStatement,
  questionValidationError,
}) => {
  const canMutate = canAdd || canUpdate || canDelete;

  const [segments, setSegments] = useState<string[]>([DEFAULT_SEGMENT]);
  const [activeSegment, setActiveSegment] = useState<string>(DEFAULT_SEGMENT);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [isAddSegmentModalOpen, setIsAddSegmentModalOpen] = useState(false);
  const [userCreatedSegments, setUserCreatedSegments] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSegments((prevSegments) => {
      if (prevSegments.length === 1 && prevSegments[0] === DEFAULT_SEGMENT && questions.length > 0) {
        const uniqueSegments = Array.from(new Set(questions.map((q) => q.segment || DEFAULT_SEGMENT)));
        return uniqueSegments.length > 0 ? uniqueSegments : [DEFAULT_SEGMENT];
      }
      return prevSegments;
    });

    if (!segments.includes(activeSegment)) {
      setActiveSegment(segments[0] || DEFAULT_SEGMENT);
    }
  }, []);

  useEffect(() => {
    setSegments((prevSegments) => {
      const uniqueQuestionSegments = new Set(questions.map((q) => q.segment || DEFAULT_SEGMENT));
      const preserved = new Set([...userCreatedSegments, ...uniqueQuestionSegments]);
      const nextSegments = Array.from(preserved);
      return nextSegments.length > 0 ? nextSegments : prevSegments;
    });
  }, [userCreatedSegments, questions]);

  const handleAddSegment = () => {
    const trimmed = newSegmentName.trim();
    if (!trimmed) return;
    if (segments.includes(trimmed)) {
      toast.error('Segment already exists.');
      return;
    }
    const nextSegments = [...segments, trimmed];
    setSegments(nextSegments);
    setUserCreatedSegments((prev) => new Set([...prev, trimmed]));
    setActiveSegment(trimmed);
    setNewSegmentName('');
    setIsAddSegmentModalOpen(false);
  };

  const handleRemoveSegment = (segName: string) => {
    setScreeningQuestions((prev) =>
      prev.filter((q) => {
        const qSegment = q.segment || DEFAULT_SEGMENT;
        return qSegment !== segName;
      }),
    );
    const nextSegments = segments.filter((s) => s !== segName);
    setSegments(nextSegments);
    setUserCreatedSegments((prev) => {
      const updated = new Set(prev);
      updated.delete(segName);
      return updated;
    });
    if (activeSegment === segName) {
      setActiveSegment(nextSegments[0] || '');
    }
  };

  return (
    <div className="space-y-4">
      {questionValidationError && canMutate && <div className="text-red-500 text-xs my-2">{questionValidationError}</div>}

      <Tabs
        type={canMutate ? 'editable-card' : 'card'}
        activeKey={activeSegment}
        onChange={setActiveSegment}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            setIsAddSegmentModalOpen(true);
          } else if (action === 'remove') {
            handleRemoveSegment(targetKey as string);
          }
        }}
        items={segments.map((seg) => {
          const activeQuestionIndices = questions
            .map((q, idx) => ({ q, idx }))
            .filter((item) => (item.q.segment || DEFAULT_SEGMENT) === seg)
            .map((item) => item.idx);

          return {
            key: seg,
            label: seg,
            closable: canMutate && segments.length > 1,
            children: (
              <div className="space-y-4 pt-3">
                {activeQuestionIndices.length === 0 ? (
                  <div className="text-gray-500 text-sm italic py-4 text-center">
                    No screening questions in this segment yet. Click &quot;Add Question&quot; below.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeQuestionIndices.map((originalIndex, displayIndex) => {
                      const question = questions[originalIndex];
                      return (
                        <div key={`screening-question-${originalIndex}`} className="border rounded-md p-4 bg-white">
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-medium text-gray-800">Question {displayIndex + 1}</div>
                              {canDelete && (
                                <Button
                                  type="text"
                                  danger
                                  aria-label={`Remove question ${displayIndex + 1}`}
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeScreeningQuestion(originalIndex)}
                                />
                              )}
                            </div>
                            <Input
                              placeholder="Enter question text"
                              value={question.question}
                              disabled={!canUpdate}
                              data-screening-field={`edit-question-${originalIndex}-question`}
                              onChange={(e) => updateScreeningQuestion(originalIndex, { question: e.target.value })}
                            />

                            {question.type === SCREENING_QUESTION.LIKERT_SCALE && (
                              <Input.TextArea
                                rows={2}
                                placeholder="Optional description for this Likert question"
                                value={question.description || ''}
                                disabled={!canUpdate}
                                onChange={(e) => updateScreeningQuestion(originalIndex, { description: e.target.value })}
                              />
                            )}

                            <div className="flex flex-wrap gap-3">
                              <Select
                                value={question.type}
                                disabled={!canUpdate}
                                onChange={(value) =>
                                  updateScreeningQuestion(originalIndex, {
                                    type: value as SCREENING_QUESTION,
                                    options:
                                      value === SCREENING_QUESTION.MULTI_CHOICE
                                        ? question.type === SCREENING_QUESTION.MULTI_CHOICE
                                          ? (question.options ?? [])
                                          : []
                                        : value === SCREENING_QUESTION.LIKERT_SCALE
                                          ? question.type === SCREENING_QUESTION.LIKERT_SCALE && question.options?.length
                                            ? question.options
                                            : DEFAULT_LIKERT_OPTIONS
                                          : undefined,
                                    statements:
                                      value === SCREENING_QUESTION.LIKERT_SCALE
                                        ? question.type === SCREENING_QUESTION.LIKERT_SCALE && question.statements?.length
                                          ? question.statements
                                          : DEFAULT_LIKERT_STATEMENTS
                                        : undefined,
                                  })
                                }
                                options={SCREENING_QUESTION_TYPE_OPTIONS}
                                style={{ minWidth: 200 }}
                              />

                              <Checkbox
                                checked={question.required}
                                disabled={!canUpdate}
                                onChange={(e) => updateScreeningQuestion(originalIndex, { required: e.target.checked })}
                              >
                                Required
                              </Checkbox>
                            </div>

                            {question.type === SCREENING_QUESTION.MULTI_CHOICE && (
                              <div className="space-y-3">
                                <div className="text-xs text-gray-500">Add options for this question.</div>
                                <div className="space-y-2">
                                  {(question.options || []).map((option, optionIndex) => (
                                    <div
                                      key={`question-${originalIndex}-option-${optionIndex}`}
                                      className="flex items-center gap-2"
                                    >
                                      <Input
                                        placeholder={`Option ${optionIndex + 1}`}
                                        value={option}
                                        disabled={!canUpdate}
                                        data-screening-field={`edit-question-${originalIndex}-option-${optionIndex}`}
                                        onChange={(e) => updateQuestionOption(originalIndex, optionIndex, e.target.value)}
                                      />
                                      {canDelete && (
                                        <Button
                                          type="text"
                                          danger
                                          aria-label={`Remove option ${optionIndex + 1}`}
                                          icon={<DeleteOutlined />}
                                          onClick={() => removeQuestionOption(originalIndex, optionIndex)}
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                  {canAdd && (
                                    <Button type="dashed" onClick={() => addQuestionOption(originalIndex)}>
                                      Add option
                                    </Button>
                                  )}
                                  <Checkbox
                                    checked={question.allowOthers ?? false}
                                    disabled={!canUpdate}
                                    onChange={(e) => updateScreeningQuestion(originalIndex, { allowOthers: e.target.checked })}
                                  >
                                    Add &quot;Others&quot; option
                                  </Checkbox>
                                </div>
                              </div>
                            )}

                            {question.type === SCREENING_QUESTION.LIKERT_SCALE && (
                              <LikertMatrix
                                mode="editable"
                                options={question.options}
                                statements={question.statements}
                                fieldIdPrefix={`edit-question-${originalIndex}`}
                                canAdd={canAdd}
                                canDelete={canDelete}
                                canUpdate={canUpdate}
                                onOptionChange={(optionIndex, value) => updateQuestionOption(originalIndex, optionIndex, value)}
                                onStatementChange={(statementIndex, value) =>
                                  updateQuestionStatement(originalIndex, statementIndex, value)
                                }
                                onAddOption={() => addQuestionOption(originalIndex)}
                                onAddStatement={() => addQuestionStatement(originalIndex)}
                                onRemoveOption={(optionIndex) => removeQuestionOption(originalIndex, optionIndex)}
                                onRemoveStatement={(statementIndex) => removeQuestionStatement(originalIndex, statementIndex)}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {canAdd && (
                  <div className="mt-4 flex justify-end">
                    <Button type="dashed" onClick={() => addScreeningQuestion(seg)} icon={<PlusOutlined />}>
                      Add Question to {seg}
                    </Button>
                  </div>
                )}
              </div>
            ),
          };
        })}
      />

      <Modal
        title="Add New Question Segment"
        open={isAddSegmentModalOpen}
        onCancel={() => setIsAddSegmentModalOpen(false)}
        onOk={handleAddSegment}
        okText="Add"
        cancelText="Cancel"
      >
        <div className="py-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Segment Name</label>
          <Input
            placeholder="e.g. Patient, Doctor, General"
            value={newSegmentName}
            onChange={(e) => setNewSegmentName(e.target.value)}
            onPressEnter={handleAddSegment}
          />
        </div>
      </Modal>
    </div>
  );
};
